import { useEffect } from 'react';

import type {
  CategoryPage,
  CustomPage,
  Page,
  StoryPage,
  TagPage,
} from '@vgtv/api-client/lib/page';
import { PageType } from '@vgtv/api-client/lib/page';
import type { SvpCategory } from '@vgtv/api-client/lib/svp_category';
import type { CnpTag } from '@vgtv/api-client/lib/cnp_tag';
import type { CnpStory } from '@vgtv/api-client/lib/cnp_story';

import { selectIsPageFetched, selectIsPageFetching } from '../modules/fetching';
import {
  fetchPageByCategoryId,
  fetchPageBySlug,
  fetchPageByStoryId,
  fetchPageByTagId,
  selectPageByCategory,
  selectPageBySlug,
  selectPageByStory,
  selectPageByTag,
} from '../modules/pages';
import { useAppSelector, useAppDispatch } from '../modules/hooks';
import type { Vendor } from '../types';

type PageConfig = {
  [PageType.CATEGORY]: {
    id: SvpCategory['id'];
    type: CategoryPage;
  };
  [PageType.TAG]: { id: CnpTag['id']; type: TagPage };
  [PageType.STORY]: {
    id: CnpStory['id'];
    type: StoryPage;
  };
  [PageType.CUSTOM]: {
    id: string;
    type: CustomPage;
  };
};

/**
 * Combines dispatch + selector to access pageConfig
 */
function usePageConfig<T extends keyof PageConfig>({
  identifier,
  pageType,
  vendor,
}: {
  identifier: PageConfig[T]['id'] | undefined;
  pageType: T;
  vendor: Vendor;
}): {
  pageConfig?: PageConfig[T]['type'];
  isFetching: boolean;
  isFetched: boolean;
} {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (identifier) {
      if (pageType === PageType.CATEGORY) {
        dispatch(
          fetchPageByCategoryId({
            categoryId: identifier as SvpCategory['id'],
            vendor,
          })
        );
      }

      if (pageType === PageType.TAG) {
        dispatch(
          fetchPageByTagId({
            tagId: identifier as CnpTag['id'],
            vendor,
          })
        );
      }

      if (pageType === PageType.STORY) {
        dispatch(
          fetchPageByStoryId({ storyId: identifier as CnpStory['id'], vendor })
        );
      }

      if (pageType === PageType.CUSTOM) {
        dispatch(
          fetchPageBySlug({
            slug: identifier as string,
            vendor,
          })
        );
      }
    }
  }, [identifier, dispatch, vendor, pageType]);

  const pageConfig = useAppSelector((state) => {
    if (pageType === PageType.CATEGORY) {
      return selectPageByCategory(
        state,
        identifier as SvpCategory['id'],
        vendor
      );
    }

    if (pageType === PageType.TAG) {
      return selectPageByTag(state, identifier as CnpTag['id'], vendor);
    }

    if (pageType === PageType.STORY) {
      return selectPageByStory(state, identifier as CnpStory['id'], vendor);
    }

    if (pageType === PageType.CUSTOM) {
      return selectPageBySlug(state, identifier as string, vendor);
    }

    return undefined;
  });

  const isFetching = useAppSelector((state) =>
    selectIsPageFetching(state, identifier as Page['id'], pageType, vendor)
  );

  const isFetched = useAppSelector((state) =>
    selectIsPageFetched(state, identifier as Page['id'], pageType, vendor)
  );

  return { pageConfig, isFetching, isFetched };
}

export { usePageConfig };
